import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import * as userApi from '../../lib/api/user';

const MAX_STEPS = 8;

/**
 * 결재선 단계 순서 편집기(ksm_frontend ApprovalStepEditor.js 포팅, 2026-10-20 결재 V2 이관).
 * 결재선 템플릿 관리 화면에서 쓴다 - 최대 8단계, 사용자 1명씩, 본인/중복 지정은 막는다.
 *
 * props:
 *  - steps: [{ assigneeUsrUuid, assigneeUsrNm }]
 *  - onChange: (steps) => void
 *  - excludeUsrUuid: 결재 단계에 넣을 수 없는 사용자(보통 본인)
 */
const ApprovalStepEditor = ({ steps, onChange, excludeUsrUuid }) => {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    userApi.getUsers().then((res) => {
      setUsers(res?.data?.list || []);
    });
  }, []);

  const selectedUuids = (steps || []).map((s) => s.assigneeUsrUuid).filter(Boolean);

  const findUser = (usrUuid) => users.find((u) => u.usrUuid === usrUuid) || null;

  const handleAdd = () => {
    if ((steps || []).length >= MAX_STEPS) return;
    onChange([...(steps || []), { assigneeUsrUuid: '', assigneeUsrNm: '' }]);
  };

  const handleRemove = (index) => {
    const next = [...steps];
    next.splice(index, 1);
    onChange(next);
  };

  const handleMove = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    onChange(next);
  };

  const handleSelect = (index, user) => {
    const next = [...steps];
    next[index] = {
      ...next[index],
      assigneeUsrUuid: user ? user.usrUuid : '',
      assigneeUsrNm: user ? user.usrNm : '',
    };
    onChange(next);
  };

  return (
    <Box>
      <Stack spacing={1.5}>
        {(steps || []).map((step, index) => {
          const current = findUser(step.assigneeUsrUuid);
          const duplicate =
            step.assigneeUsrUuid &&
            selectedUuids.filter((u) => u === step.assigneeUsrUuid).length > 1;
          const isSelf = excludeUsrUuid && step.assigneeUsrUuid === excludeUsrUuid;
          return (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 56 }}>
                {index + 1}단계
              </Typography>
              <Autocomplete
                sx={{ flex: 1 }}
                size="small"
                options={users.filter((u) => u.usrUuid !== excludeUsrUuid)}
                getOptionLabel={(option) => option?.usrNm || ''}
                isOptionEqualToValue={(option, value) => option.usrUuid === value?.usrUuid}
                value={current}
                onChange={(event, value) => handleSelect(index, value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="결재자 선택"
                    error={Boolean(duplicate || isSelf)}
                    helperText={
                      isSelf
                        ? '본인은 결재 단계에 넣을 수 없습니다.'
                        : duplicate
                          ? '같은 결재자가 중복되었습니다.'
                          : ''
                    }
                  />
                )}
              />
              <IconButton
                size="small"
                disabled={index === 0}
                onClick={() => handleMove(index, -1)}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={index === steps.length - 1}
                onClick={() => handleMove(index, 1)}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => handleRemove(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        })}
      </Stack>
      <Button
        sx={{ mt: 1.5 }}
        size="small"
        startIcon={<AddIcon />}
        disabled={(steps || []).length >= MAX_STEPS}
        onClick={handleAdd}
      >
        단계 추가
      </Button>
      {(steps || []).length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          최소 1명의 결재 단계를 추가하세요.
        </Typography>
      )}
    </Box>
  );
};

export default ApprovalStepEditor;
